import { useState, useRef, useEffect } from "react";
import { Receipt, FileText, Download, Calendar, DollarSign, Layers } from "lucide-react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { generatePDF, generateCSV } from "../../components/deposit-summary/utils";
import StatCard from "../../components/ui/StatCard";
import EmptyState from "../../components/ui/EmptyState";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl } from "../../utils/api";

import "react-datepicker/dist/react-datepicker.css";

const presets = [
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'lastQuarter', label: 'Last Quarter' },
    { key: 'lastYear', label: 'Previous Year'},
    { key: 'ytd', label: 'YTD' },
    { key: 'custom', label: 'Custom' },
];

const DepositSummaryPage = ({ currentUser, currentDistro, onSessionExpired, onTokenRefresh }) => {
    const [activePreset, setActivePreset] = useState('custom');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [depositSummary, setDepositSummary] = useState([]);
    const [selectedType, setSelectedType] = useState("separated");
    const [distroAccountName, setDistroAccountName] = useState("");
    const [summaryTotal, setSummaryTotal] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const toastIdRef = useRef(null);

    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const portal = () => {
        const { innerWidth: width } = window;
        return width <= 500;
    };

    const getPresetDates = (preset) => {
        const now = new Date();
        let start, end;

        switch (preset) {
            case 'lastMonth': {
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            }
            case 'lastQuarter': {
                const currentQuarter = Math.floor(now.getMonth() / 3);
                const lastQuarterStart = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3;
                const yearOffset = currentQuarter === 0 ? -1 : 0;
                start = new Date(now.getFullYear() + yearOffset, lastQuarterStart, 1);
                end = new Date(now.getFullYear() + yearOffset, lastQuarterStart + 3, 0);
                break;
            }
            case 'lastYear': {
                const lastYear = now.getFullYear() - 1;
                start = new Date(lastYear, 0, 1);
                end = new Date(lastYear, 11, 31);
                break;
            }
            case 'ytd': {
                start = new Date(now.getFullYear(), 0, 1);
                end = now;
                break;
            }
            default:
                return { start: null, end: null };
        }

        return { start, end };
    };

    const handlePresetClick = (preset) => {
        setActivePreset(preset);
        if (preset !== 'custom') {
            const { start, end } = getPresetDates(preset);
            setFromDate(start);
            setToDate(end);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        toastIdRef.current = toast.loading('Loading deposits...');

        try {
            const data = await fetchDepositSummaryData();
            setDepositSummary(data.results);
            setDistroAccountName(data.distroAccountName);
            setHasSearched(true);
            toast.dismiss(toastIdRef.current);
        } catch (err) {
            toast.error("No deposit payments for this date range.", { id: toastIdRef.current });
        }
    };

    useEffect(() => {
        if (Array.isArray(depositSummary)) {
            setSummaryTotal(depositSummary.reduce((sum, dep) => sum + (dep.amount || 0), 0));
        } else if (typeof depositSummary === "object" && depositSummary !== null) {
            const total = Object.values(depositSummary)
                .flat()
                .reduce((sum, dep) => sum + (dep.amount || 0), 0);
            setSummaryTotal(total);
        } else {
            setSummaryTotal(0);
        }
    }, [depositSummary]);

    const fetchDepositSummaryData = async () => {
        const url = new URL(`${apiUrl}/financials/getdepositsummary`);
        url.searchParams.append("distroId", currentDistro);
        url.searchParams.append("dateFrom", formatDate(fromDate));
        url.searchParams.append("dateTo", formatDate(toDate));
        url.searchParams.append("selectedType", selectedType);

        const response = await authFetch(url.toString(), {}, authParams);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(response.statusText || "Failed to fetch deposit summary");
        }
        return data;
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const downloadDepositPDF = async (dep) => {
        const pdfToastId = toast.loading('Generating PDF...');
        try {
            let payment_results = {};
            if (!Array.isArray(dep)) {
                const accountType = getAccountType(dep.notes);
                const results = await fetchCommissionData(dep.paymentFileId, accountType);
                generatePDF(distroAccountName, [dep], results);
            } else {
                for (const d of dep) {
                    const accountType = getAccountType(d.notes);
                    const perType = await fetchCommissionData(d.paymentFileId, accountType);
                    payment_results[accountType] = perType;
                }
                generatePDF(distroAccountName, dep, payment_results);
            }
            toast.dismiss(pdfToastId);
        } catch (err) {
            toast.error("No commissions found for this payment.", { id: pdfToastId });
        }
    };

    const downloadDepositCSV = async (dep) => {
        const csvToastId = toast.loading('Generating CSV...');

        try {
            let payment_results = {};
            let rows = [];

            if (!Array.isArray(dep)) {
                const accountType = getAccountType(dep.notes);
                const results = await fetchCommissionData(dep.paymentFileId, accountType);

                payment_results[accountType] = results;

                rows = formatCSVRows([dep], payment_results);
            } else {
                for (const d of dep) {
                    const accountType = getAccountType(d.notes);
                    const perType = await fetchCommissionData(d.paymentFileId, accountType);
                    payment_results[accountType] = perType;
                }

                rows = formatCSVRows(dep, payment_results);
            }

            generateCSVFile(distroAccountName, rows);

            toast.dismiss(csvToastId);
        } catch (err) {
            toast.error("No commissions found for this payment.", { id: csvToastId });
        }
    };

    const formatCSVRows = (deposits, paymentResults) => {
        const rows = [];

        deposits.forEach(dep => {
            const accountType = getAccountType(dep.notes);
            const commissions = paymentResults[accountType] || [];

            commissions.forEach(comm => {
                rows.push({
                    Name: comm.companyName + " (" + comm.policyNo + ")",
                    Amount: comm.totalTransAmount
                });
            });
        });

        return rows;
    };

    const generateCSVFile = (fileName, rows) => {
        if (!rows.length) {
            toast.error("No data available for CSV export.");
            return;
        }

        const headers = Object.keys(rows[0]);

        const csvContent = [
            headers.join(','), 
            ...rows.map(row =>
                headers.map(field => `"${(row[field] ?? '').toString().replace(/"/g, '""')}"`).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}_commissions.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const downloadSummaryPDF = async () => {
        const pdfToastId = toast.loading('Generating PDF...');
        try {
            let payment_results = { "spending": [], "corehealth": [], "teladoc": [] };
            const dateRange = `${formatDate(fromDate)} - ${formatDate(toDate)}`;

            if (!Array.isArray(depositSummary)) {
                for (const [date, payments] of Object.entries(depositSummary)) {
                    if (!Array.isArray(payments)) continue;
                    for (const payment of payments) {
                        const accountType = getAccountType(payment.notes);
                        const result = await fetchCommissionData(payment.paymentFileId, accountType);
                        payment_results[accountType] = payment_results[accountType].concat(result);
                    }
                }
                generatePDF(distroAccountName, depositSummary, payment_results, dateRange);
            } else {
                for (const d of depositSummary) {
                    const accountType = getAccountType(d.notes);
                    const perType = await fetchCommissionData(d.paymentFileId, accountType);
                    payment_results[accountType] = payment_results[accountType].concat(perType);
                }
                generatePDF(distroAccountName, depositSummary, payment_results, dateRange);
            }
            toast.dismiss(pdfToastId);
        } catch (err) {
            toast.error("Error generating PDF.", { id: pdfToastId });
        }
    };

    const getAccountType = (notes) => {
        if (notes?.includes("corehealth")) return "corehealth";
        if (notes?.includes("teladoc")) return "teladoc";
        return "spending";
    };

    const fetchCommissionData = async (paymentFileId, accountType = null) => {
        const url = new URL(`${apiUrl}/financials/getpaymentcommissions`);
        url.searchParams.append("distroId", currentDistro);
        url.searchParams.append("paymentFileId", paymentFileId);
        url.searchParams.append("selectedType", selectedType);
        if (accountType) {
            url.searchParams.append("accountType", accountType);
        }

        const response = await authFetch(url.toString(), {}, authParams);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(response.statusText || "Failed to fetch commissions");
        }

        return data.results;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const formatDateDisplay = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const hasData = depositSummary && (
        (Array.isArray(depositSummary) && depositSummary.length > 0) ||
        (!Array.isArray(depositSummary) && Object.keys(depositSummary).length > 0)
    );

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                {/* page header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-xl">
                        <Receipt className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                            Deposit Summary
                        </h1>
                        <p className="text-sm text-gray-500">
                            View deposit payment history
                        </p>
                    </div>
                </div>

                {/* filter card */}
                <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                    <form onSubmit={handleSubmit}>
                        {/* preset buttons */}
                        <div className="flex flex-wrap items-center gap-2 mb-4 min-h-9">
                            {presets.map(preset => (
                                <button
                                    key={preset.key}
                                    type="button"
                                    onClick={() => handlePresetClick(preset.key)}
                                    className={`h-9 px-3 sm:px-4 text-sm sm:min-w-[100px] rounded-full font-medium transition-colors ${
                                        activePreset === preset.key
                                            ? 'bg-[#1e85e5] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>

                        {/* filters - stack on mobile, row on desktop */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-end gap-3">
                            <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-gray-400 hidden sm:block" />
                                <select
                                    className="h-10 px-4 w-full sm:w-auto border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    value={selectedType}
                                >
                                    <option value="separated">Per Product</option>
                                    <option value="consolidated">Consolidated</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2 col-span-1 sm:col-span-2 lg:col-span-1">
                                <Calendar className="w-4 h-4 text-gray-400 hidden sm:block" />
                                <DatePicker
                                    className="h-10 w-full sm:w-28 lg:w-32 px-3 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                    selected={fromDate}
                                    onChange={(date) => {
                                        setFromDate(date);
                                        setActivePreset('custom');
                                    }}
                                    placeholderText="Start"
                                    required
                                    withPortal={portal()}
                                    disabledKeyboardNavigation={portal()}
                                />
                                <span className="text-gray-400">to</span>
                                <DatePicker
                                    className="h-10 w-full sm:w-28 lg:w-32 px-3 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                    selected={toDate}
                                    onChange={(date) => {
                                        setToDate(date);
                                        setActivePreset('custom');
                                    }}
                                    placeholderText="End"
                                    required
                                    withPortal={portal()}
                                    disabledKeyboardNavigation={portal()}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={!fromDate || !toDate}
                                className="h-10 px-5 flex items-center w-full sm:w-auto bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Apply Filter
                            </button>
                        </div>
                    </form>
                </div>

                {/* tax responsibility notice */}
                <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl text-sm text-amber-900 leading-relaxed">
                    <span className="font-semibold">Important:</span> Advisors, or the Advisor's registered business entity, are responsible for reporting and remitting any applicable income or business taxes related to commissions. <span className="font-semibold">Please note that NHC does not provide T4 slips.</span>
                </div>

                {/* results */}
                {hasSearched && hasData && (
                    <>
                        {/* summary stat */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <StatCard
                                icon={DollarSign}
                                label="Total Deposits"
                                value={formatCurrency(summaryTotal)}
                                sublabel={fromDate && toDate ? `${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}` : ''}
                                variant="primary"
                            />
                        </div>

                        {/* table */}
                        <div className="bg-white rounded-2xl shadow-card p-6">
                            {/* header */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                                    Deposit Details
                                </h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            const dateRange = `${formatDate(fromDate)} - ${formatDate(toDate)}`;
                                            generateCSV(distroAccountName, depositSummary, dateRange);
                                        }}
                                        className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                        title="Download CSV"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>CSV</span>
                                    </button>
                                    <button
                                        onClick={() => downloadSummaryPDF()}
                                        className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                        title="Download PDF"
                                    >
                                        <FileText className="w-4 h-4" />
                                        <span>PDF</span>
                                    </button>
                                </div>
                            </div>

                            {/* table */}
                            <div className="overflow-x-auto">
                                <table className="table-fixed w-full">
                                    <thead>
                                        <tr className="border-b border-gray-100">
                                            <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[45%] sm:w-[55%]">
                                                Date
                                            </th>
                                            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[20%] sm:w-[15%]">
                                                Amount
                                            </th>
                                            <th className="w-[35%] sm:w-[30%]"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {(() => {
                                            const rows = selectedType === "consolidated" && !Array.isArray(depositSummary)
                                                ? Object.entries(depositSummary ?? {}).map(([date, payments]) => {
                                                    if (!Array.isArray(payments)) return null;
                                                    return {
                                                        key: date,
                                                        label: moment(date).format("MMMM DD, YYYY"),
                                                        amount: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
                                                        data: payments,
                                                    };
                                                }).filter(Boolean)
                                                : selectedType === "separated" && Array.isArray(depositSummary)
                                                ? depositSummary.map((dep, id) => ({
                                                    key: id,
                                                    label: `${moment(dep.createdAt).format("MMMM DD, YYYY")} (${
                                                        dep.notes?.includes("corehealth") ? "CoreHealth"
                                                        : dep.notes?.includes("teladoc") ? "Teladoc"
                                                        : "Spending Account"
                                                    })`,
                                                    amount: dep.amount,
                                                    data: dep,
                                                }))
                                                : [];

                                            return rows.map(({ key, label, amount, data }) => (
                                                <tr key={key} className="hover:bg-gray-50 transition-colors">
                                                    <td className="py-3 px-2 w-[45%] sm:w-[55%] font-medium text-gray-900">
                                                        <button
                                                            onClick={() => downloadDepositPDF(data)}
                                                            onMouseEnter={() => setHoveredIndex(key)}
                                                            onMouseLeave={() => setHoveredIndex(null)}
                                                            className={`text-left font-medium transition-colors ${
                                                                hoveredIndex === key ? "text-primary" : "text-gray-900"
                                                            } hover:text-primary`}
                                                        >
                                                            {label}
                                                        </button>
                                                    </td>
                                                    <td className="py-3 px-2 w-[20%] sm:w-[15%] text-right font-medium text-gray-900">
                                                        {formatCurrency(amount)}
                                                    </td>
                                                    <td className="w-[35%] sm:w-[30%] text-right font-medium text-gray-900">
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <button
                                                                onClick={() => downloadDepositCSV(data)}
                                                                className="h-9 px-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                                                title="Download CSV"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                                <span className="hidden sm:inline">CSV</span>
                                                            </button>
                                                            <button
                                                                onClick={() => downloadDepositPDF(data)}
                                                                className="h-9 px-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                                                title="Download PDF"
                                                            >
                                                                <FileText className="w-4 h-4" />
                                                                <span className="hidden sm:inline">PDF</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ));
                                        })()}

                                        <tr className="border-t border-gray-200 bg-gray-50">
                                            <td className="py-3 px-2 font-semibold text-gray-900">
                                                Total
                                            </td>
                                            <td className="py-3 px-2 text-right font-bold text-primary">
                                                {formatCurrency(summaryTotal)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* empty state */}
                {hasSearched && !hasData && (
                    <div className="bg-white rounded-2xl shadow-card p-6">
                        <EmptyState
                            icon={Receipt}
                            title="No deposits found"
                            description="Try adjusting your date range to see deposit payment history."
                        />
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DepositSummaryPage);
